import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { getSocketConfig } from '../utils/socketConfig';
import {authAPI} from "../services/api";

interface SocketContextValue {
    socket: Socket | null;
    isConnected: boolean;
    hasReconnected: boolean;
    reconnectionFailed: boolean;
    manualReconnect: () => void;
}

const SocketContext = createContext<SocketContextValue | undefined>(undefined);

export const SocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [hasReconnected, setHasReconnected] = useState(false);
    const [reconnectionFailed, setReconnectionFailed] = useState(false);

    const socketRef = useRef<Socket | null>(null);
    const initialConnectionRef = useRef(true);
    const lastActivityRef = useRef<number>(Date.now());
    const { user, token } = useAuth();
    const tokenRef = useRef(token);

    useEffect(() => {
        const oldToken = tokenRef.current;
        tokenRef.current = token;

        if (socketRef.current?.connected && token && oldToken && token !== oldToken) {
            socketRef.current.auth = { token };
        }
    }, [token]);

    const createSocket = () => {
        if (socketRef.current) {
            socketRef.current.removeAllListeners();
            socketRef.current.disconnect();
            socketRef.current = null;
        }


        const config = getSocketConfig();

        const newSocket = io(process.env.REACT_APP_CHAT_URL || 'http://localhost:5000', {
            auth: { token: tokenRef.current }, // ← Use tokenRef instead of token closure
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: config.reconnectionDelay,
            reconnectionDelayMax: config.reconnectionDelayMax,
            reconnectionAttempts: config.reconnectionAttempts,
            timeout: config.timeout,
            autoConnect: true
        });

        newSocket.on('connect', () => {
            setIsConnected(true);
            setReconnectionFailed(false);
            lastActivityRef.current = Date.now();

            if (!initialConnectionRef.current) {
                setHasReconnected(true);
                setTimeout(() => setHasReconnected(false), 100);
            } else {
                initialConnectionRef.current = false;
            }
        });

        newSocket.on('disconnect', (reason) => {
            setIsConnected(false);
            lastActivityRef.current = Date.now();

            if (reason === 'transport error' || reason === 'transport close') {
                console.log('[SocketContext] Transport issue - connection lost');
            }
        });

        newSocket.on('connect_error', (error) => {
            console.error('[SocketContext] Connection error:', error.message);
            setIsConnected(false);
            lastActivityRef.current = Date.now();
        });

        newSocket.on('reconnect_attempt', (attemptNumber) => {
            const authToken = (newSocket.auth as { token?: string })?.token;
        });

        newSocket.on('reconnect_failed', () => {
            setReconnectionFailed(true);
        });

        newSocket.on('reconnect', (attemptNumber) => {
            console.log('[SocketContext] 🎉 Reconnected successfully after', attemptNumber, 'attempts');
        });

        newSocket.on('error', (error) => {
            console.error('[SocketContext] Socket error:', error);
        });

        socketRef.current = newSocket;
        setSocket(newSocket);

        if (process.env.NODE_ENV === 'development') {
            (window as any).__socket = newSocket;
        }
    };

    const manualReconnect = async () => {
        console.log('[SocketContext] Manual reconnect triggered');
        setReconnectionFailed(false);

        const currentToken = localStorage.getItem('accessToken');

        if (!currentToken) {
            console.error('[SocketContext] No token found, cannot reconnect');
            setReconnectionFailed(true);
            return;
        }

        console.log('[SocketContext] Verifying token validity...');
        try {
            await authAPI.getProfile();
        } catch (error: any) {
            if (error.response?.status === 401) {
                console.error('[SocketContext] Cannot refresh token');
                setReconnectionFailed(true);
                return;
            }
        }

        const freshToken = localStorage.getItem('accessToken');
        tokenRef.current = freshToken;

        if (socketRef.current) {
            socketRef.current.removeAllListeners();
            socketRef.current.disconnect();
            socketRef.current = null;
            setSocket(null);
        }

        createSocket();
    };

    useEffect(() => {
        if (!user || !token) {
            if (socketRef.current) {
                socketRef.current.removeAllListeners();
                socketRef.current.disconnect();
                socketRef.current = null;
                setSocket(null);
                setIsConnected(false);
                initialConnectionRef.current = true;
            }
            return;
        }

        if (socketRef.current?.connected) {
            return;
        }

        if (socketRef.current && !socketRef.current.connected) {
            socketRef.current.removeAllListeners();
            socketRef.current.disconnect();
            socketRef.current = null;
        }

        createSocket();

        return () => {
            if (socketRef.current) {
                socketRef.current.removeAllListeners();
                socketRef.current.disconnect();
                socketRef.current = null;
                setSocket(null);
                setIsConnected(false);
                initialConnectionRef.current = true;
            }
        };
    }, [user?.id]);

    useEffect(() => {
        const handleFocus = () => {
            const now = Date.now();
            const timeSinceLastActivity = now - lastActivityRef.current;

            if (socketRef.current) {
                const isConnected = socketRef.current.connected;

                if (!isConnected) {
                    setReconnectionFailed(false);
                    manualReconnect();
                } else if (timeSinceLastActivity > 30000) {
                    socketRef.current.emit('ping', (response: any) => {
                        lastActivityRef.current = now;
                    });

                    const pingTimeout = setTimeout(() => {
                        if (socketRef.current) {
                            socketRef.current.disconnect();
                            manualReconnect();
                        }
                    }, 5000);

                    const clearPingTimeout = () => clearTimeout(pingTimeout);
                    socketRef.current.once('pong', clearPingTimeout);
                    socketRef.current.once('message', clearPingTimeout);
                }
            } else {
                if (user && token) {
                    createSocket();
                }
            }

            lastActivityRef.current = now;
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                handleFocus();
            } else {
                lastActivityRef.current = Date.now();
            }
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [user, token]);

    useEffect(() => {
        const handleOnline = () => {
            if (socketRef.current && !socketRef.current.connected) {
                manualReconnect();
            }
        };

        const handleOffline = () => {
            if (socketRef.current?.connected) {
                socketRef.current.disconnect();
            }
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    return (
        <SocketContext.Provider value={{
            socket,
            isConnected,
            hasReconnected,
            reconnectionFailed,
            manualReconnect
        }}>
            {children}
        </SocketContext.Provider>
    );
};

export const useSocket = (): SocketContextValue => {
    const context = useContext(SocketContext);
    if (context === undefined) {
        throw new Error('useSocket must be used within a SocketProvider');
    }
    return context;
};